import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FB_BRAND } from "@/lib/apiClient";
import { ArrowLeft, Menu, X, LogOut, ExternalLink } from "lucide-react";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const homePath = isAdmin ? "/espace/admin" : "/mon-espace";
  const isHome = location.pathname === homePath;
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);
  const handleLogout = async () => {
    closeMenu();
    await logout();
    navigate("/");
  };
  const onBack = () => navigate(homePath);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 font-body">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <a
              href={FB_BRAND.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-logo-link"
              title={`Voir le site ${FB_BRAND.name}`}
              className="relative shrink-0 group active:scale-95 transition-transform"
            >
              <img
                src={FB_BRAND.logo}
                alt={FB_BRAND.name}
                className="w-11 h-11 object-contain rounded-2xl bg-white p-1 shadow-md group-hover:shadow-lg transition-all"
              />
              <span className="absolute -bottom-1 -right-1 bg-white border border-stone-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                <ExternalLink className="w-2.5 h-2.5 text-stone-700" strokeWidth={3} />
              </span>
            </a>
            <Link to={homePath} className="leading-tight min-w-0 flex-1" data-testid="header-brand-text">
              <div className="font-heading font-black text-base sm:text-lg tracking-tight truncate" data-testid="header-brand-name">
                {FB_BRAND.name}
              </div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-stone-500 font-bold truncate">
                {FB_BRAND.tagline}
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="header-menu-btn"
              aria-label="Ouvrir le menu"
              className="p-2 rounded-full hover:bg-stone-100 active:scale-95 transition-all"
            >
              <Menu className="w-6 h-6 text-stone-700" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Drawer latéral */}
      {menuOpen && (
        <div className="fixed inset-0 z-50" data-testid="header-menu-drawer">
          <button
            type="button"
            onClick={closeMenu}
            aria-label="Fermer le menu"
            className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
          />
          <aside className="absolute top-0 right-0 h-full w-80 max-w-[88vw] bg-white shadow-2xl flex flex-col overflow-y-auto">
            <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3">
              <div className="font-heading font-black text-lg">Menu</div>
              <button
                type="button"
                onClick={closeMenu}
                data-testid="header-menu-close"
                aria-label="Fermer"
                className="p-1.5 rounded-full hover:bg-stone-100"
              >
                <X className="w-5 h-5 text-stone-600" strokeWidth={2.5} />
              </button>
            </div>

            {/* Infos commercial */}
            {user && (
              <div className="p-5 border-b border-stone-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-stone-400">
                  Connecté en tant que
                </div>
                <div className="font-heading font-black text-base mt-1 truncate" data-testid="menu-user-name">
                  {user.name || user.email}
                </div>
                <div className="text-xs text-stone-500 truncate">{user.email}</div>
              </div>
            )}

            <div className="flex-1" />

            {/* Logout */}
            <div className="p-5 border-t border-stone-100">
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-stone-100 hover:bg-rose-50 text-stone-700 hover:text-rose-700 font-bold px-4 py-3 rounded-2xl transition-colors"
              >
                <LogOut className="w-4 h-4" strokeWidth={2.5} /> Déconnexion
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Page content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-12 pt-4 sm:pt-6">
        {!isHome && (
          <div className="mb-3">
            <button
              type="button"
              onClick={onBack}
              data-testid="header-back"
              aria-label="Retour à la grille tarifaire"
              className="group inline-flex items-center gap-1.5 text-stone-500 hover:text-[#1F6B2E] text-sm font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" strokeWidth={1.5} />
              <span className="lowercase tracking-wide">retour à la grille tarifaire</span>
            </button>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
